import { JobAppQuestionAnswerRequest } from "../../../../api-docs";
import { SingleSelectEdit } from "./single-select-edit";
import { MultiSelectEdit } from "./multi-select-edit";
import { FreeTextEdit } from "./free-text-edit";
import { NumericalEdit } from "./numerical-edit";
import { DateQuestionEdit } from "./date-question-edit";
import { JobQuestionNode } from "../../../../graphql.types";
import { orderBy } from "lodash";
import { MultiFieldSet } from "@/components/utils/useFieldSet";
import { BasicErrorBoundary } from "@/flokit/utils/error-boundaries/basic-error-boundary";

const SECTION_MAP = {
  SINGLE_SELECT: SingleSelectEdit,
  MULTI_SELECT: MultiSelectEdit,
  FREE_TEXT: FreeTextEdit,
  NUMERICAL: NumericalEdit,
  DATE: DateQuestionEdit,
};

const getAnswerChoicesForSelectQuestions = (question: JobQuestionNode) => {
  const edges = question?.jobQuestionOptions?.edges ?? [];

  return edges.map((e) => {
    return {
      ...e?.node,
      jobAppQuestionAnswerId: e?.node?.id,
      value: e?.node?.orgQuestionOptionId?.toString() ?? "",
      label: e?.node?.option ?? "",
    };
  });
};

export const AppQuestionFragment = (
  props: {
    questions: JobQuestionNode[];
  } & MultiFieldSet<JobAppQuestionAnswerRequest>,
) => {
  const form = props;
  const qs = orderBy(props.questions, "order", "asc");

  return (
    <BasicErrorBoundary>
      <div className="space-y-6">
        <div className="space-y-6">
          {qs?.map((question) => {
            const valueIndex = form.values?.findIndex(
              (x) => x.orgQuestionId === question.orgQuestionId,
            );

            if (!question || !question.questionType) {
              return null;
            }

            if (question.questionType === "DATE") {
              return (
                <DateQuestionEdit
                  label={question.label}
                  description={question.subText}
                  key={question.id}
                  isRequired={question?.isRequired}
                  value={form.values[valueIndex]?.dateValue ?? ""}
                  onChange={(v) => {
                    form.setValue(valueIndex, "dateValue", v);
                  }}
                  onBlur={props.onBlur("dateValue")}
                  shouldShowValidation={
                    form.validation?.[valueIndex]?.dateValue
                  }
                  validationMessage={
                    form.validationMsg?.[valueIndex]?.dateValue
                  }
                />
              );
            }

            if (question.questionType === "FREE_TEXT") {
              return (
                <FreeTextEdit
                  label={question.label}
                  description={question.subText}
                  key={question.id}
                  isRequired={question?.isRequired}
                  value={form.values[valueIndex]?.textValue}
                  onBlur={props.onBlur("dateValue")}
                  onChange={(v) => {
                    form.setValue(valueIndex, "textValue", v);
                  }}
                  shouldShowValidation={
                    form.validation?.[valueIndex]?.textValue
                  }
                  validationMessage={
                    form.validationMsg?.[valueIndex]?.textValue
                  }
                />
              );
            }

            if (question.questionType === "NUMERICAL") {
              return (
                <NumericalEdit
                  label={question.label}
                  description={question.subText}
                  key={question.id}
                  isRequired={question?.isRequired}
                  onBlur={props.onBlur("dateValue")}
                  value={`${form.values[valueIndex]?.numericValue}`}
                  onChange={(v) => {
                    form.setValue(valueIndex, "numericValue", v);
                  }}
                  shouldShowValidation={
                    form.validation?.[valueIndex]?.numericValue
                  }
                  validationMessage={
                    form.validationMsg?.[valueIndex]?.numericValue
                  }
                />
              );
            }

            if (question.questionType === "SINGLE_SELECT") {
              const choices = getAnswerChoicesForSelectQuestions(question);
              const userAnswers =
                form?.values?.[valueIndex]?.jobAppQuestionAnswerSelections ??
                [];
              const selectedOption = choices.find((o) => {
                return (
                  userAnswers.find(
                    (a) =>
                      a != null &&
                      a.orgQuestionOptionId?.toString() === o.value,
                  ) ?? null
                );
              });

              return (
                <SingleSelectEdit
                  subText={question?.subText}
                  label={question?.label}
                  key={question.id}
                  onBlur={props.onBlur("jobAppQuestionAnswerSelections")}
                  options={choices}
                  isRequired={question.isRequired}
                  value={selectedOption}
                  validationMessage={
                    props.validationMsg?.[valueIndex]
                      ?.jobAppQuestionAnswerSelections
                  }
                  shouldShowValidation={
                    props.validation?.[valueIndex]
                      ?.jobAppQuestionAnswerSelections
                  }
                  onChange={(v) => {
                    props.setValue(
                      valueIndex,
                      "jobAppQuestionAnswerSelections",
                      v != null ? [v] : [],
                    );
                  }}
                  isInModal={false}
                />
              );
            }

            if (question.questionType === "MULTI_SELECT") {
              const choices = getAnswerChoicesForSelectQuestions(question);
              const userAnswers =
                form?.values?.[valueIndex]?.jobAppQuestionAnswerSelections ??
                [];
              const selectedOptions = userAnswers.map((userAnswer) => {
                return choices.find((option) => {
                  return (
                    option.orgQuestionOptionId ===
                    userAnswer.orgQuestionOptionId
                  );
                });
              });

              return (
                <MultiSelectEdit
                  subText={question?.subText}
                  label={question?.label}
                  key={question.id}
                  onBlur={props.onBlur("jobAppQuestionAnswerSelections")}
                  options={choices}
                  isRequired={question.isRequired}
                  value={selectedOptions}
                  validationMessage={
                    props.validationMsg?.[valueIndex]
                      ?.jobAppQuestionAnswerSelections
                  }
                  shouldShowValidation={
                    props.validation?.[valueIndex]
                      ?.jobAppQuestionAnswerSelections
                  }
                  onChange={(v) => {
                    props.setValue(
                      valueIndex,
                      "jobAppQuestionAnswerSelections",
                      v != null ? v : [],
                    );
                  }}
                  isInModal={false}
                />
              );
            }

            // We should never be here, if we are we can't assume that the form section is valid
            throw new Error(
              `Invalid question type: ${question.questionType} for question: ${question.id}`,
            );
          })}
        </div>
      </div>
    </BasicErrorBoundary>
  );
};
